import React, {
  useState,
  useEffect
} from "react";
import * as _ from "lodash";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";

import { IStore } from "src/core/shared/store/index";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import Grid from "src/library/components/Hedron";
import { Datetime } from "src/library/components/Datetime";
import { WysiwygEditorRef } from "src/library/components/WysiwygEditor";
import { Icons } from "src/library/components/Icon/types";
import { PaginatedParams } from "src/core/shared/api";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import {
  ISupportTicket,
  useSupportApi,
  ISupportTicketMessage,
  UpdateSupportTicketParams,
  SupportTicketStatus,
  CloseSupportTicketParams,
  SupportTicketPriority
} from "src/modules/support/api/support.api";
import { IUser } from "src/core/users/api/users.api";
import RespondTextAreaContainer from "src/modules/support/components/RespondTextAreaContainer";
import SupportTicketMessage from "src/modules/support/components/SupportTicketMessage";
import { formatUserName } from "src/core/users/utils/format-user-name";
import CreditRequestContextProvider, { CreditRequestContextProviderRef } from "src/modules/credit-request/context/CreditRequestContext";
import SupportTicketDetails from "src/modules/support/components/SupportTicketDetails";
import { formatSupportTicketNumber } from "src/modules/support/utils/format-support-ticket-number";
import SupportTicketSidebar, { SupportTicketSidebarUpdate } from "src/modules/support/components/SupportTicketSidebar";
import InfoBarWrapper from "src/core/shared/components/Layout/InfoBarWrapper";
import OrderStatus from "src/core/orders/components/OrderStatus";
import { DropZoneRef } from "src/core/shared/components/dropzones/DropZoneMultiFile";
import { SupportTicketInternalNoteModal } from "src/modules/support/components/SupportTicketInternalNoteModal";
import { rgba } from "src/library/utils";
import Input from "src/library/components/Forms/Input";
import { useCurrentUser } from "src/core/users/hooks";

const ViewSupportTicketPage: React.ReactFCWithChildren<PageProps<unknown, { supportTicketId: string; }>> = ({ ...props }) => {
  const { history, match } = props;
  const [ supportTicket, setSupportTicket ] = useState<ISupportTicket>();
  const [ userSubscribed, setUserSubscribed ] = useState<boolean>(true);
  // Use sanitisedListTicketMessagesRes! listTicketMessages needs to be sanitised, to ensure not displaying internal notes to customer user.
  const [ listTicketMessagesRes, listTicketMessagesReq ] = useSupportApi("LIST_TICKET_MESSAGES");
  const [ getSupportTicketRes, getSupportTicketReq ] = useSupportApi("GET_SUPPORT_TICKET");
  const [ updateSupportTicketRes, updateSupportTicketReq ] = useSupportApi("UPDATE_SUPPORT_TICKET");
  const [ closeSupportTicketRes, closeSupportTicketReq ] = useSupportApi("CLOSE_SUPPORT_TIKCET");
  const [ ticketMessages, setTicketMessages ] = useState<Array<ISupportTicketMessage & {user?: IUser}>>([]);
  const supportTicketId = match ? parseInt(match.params.supportTicketId, 0) : 0;
  const [ respondText, setRespondText ] = useState("");
  const [ timeTaken, setTimeTaken ] = useState<number | null>(null);
  const [ isSubmittingMsg, setIsSubmittingMsg ] = useState(false);
  const [ ticketMessagesCount, setTicketMessagesCount ] = useState(0);
  const [ attachmentKeys, setAttachmentKeys ] = useState<string[]>([]);
  const [ showNewInternalNoteModal, setShowNewInternalNoteModal ] = React.useState(false);
  const CreditsContextRef = React.useRef<CreditRequestContextProviderRef>(null);
  const MessageRef = React.useRef<WysiwygEditorRef>(null);
  const dropzoneRef = React.useRef<DropZoneRef>(null);

  const { currentUser, orderStatuses } = useMappedState(({ global }: IStore) => ({
    currentUser: global.user as IUser,
    orderStatuses: global.settings?.orderStatuses
  }));

  const [ { currentRole } ] = useCurrentUser();

  const [ pagination, setPagination ] = useState<PaginatedParams<ISupportTicketMessage>>({
    limit: 5,
    offset: 0,
    sort: [ [ "createdAt", "DESC" ] ],
    page: 0
  });

  const supportTicketTypeAllowsCredits = React.useMemo(() => (
    supportTicket?.supportTicketType?.options.allowCredits || false
  ), [ supportTicket?.supportTicketType?.options.allowCredits ]);

  const currentOrderStatus = React.useMemo(() => {
    return _.find(orderStatuses, os => os.id === supportTicket?.order?.orderStatusId);
  }, [ orderStatuses, supportTicket?.order?.orderStatusId ]);

  // sanitise incoming messages from api - ensuring internal notes removed if current user not admin
  const sanitisedListTicketMessagesRes = React.useMemo(() => {
    if (listTicketMessagesRes.data && currentRole?.type !== "admin") {
      // ensure internal admin private notes removed!
      const sanitisedData = listTicketMessagesRes.data.result.filter(m => m.internal !== true);

      return {
        ...listTicketMessagesRes,
        data: {
          result: sanitisedData || [],
          count: sanitisedData.length || 0,
          pages: listTicketMessagesRes.data.pages
        }
      };
    }

    return listTicketMessagesRes;
  }, [ currentRole?.type, listTicketMessagesRes ]);

  // onSuccess handler for adding new private internal note
  const handleInternalNoteSuccess = React.useCallback(() => {
    setShowNewInternalNoteModal(false);

    listTicketMessagesReq({
      supportTicketId,
      pagination,
      currentUserRole: currentRole?.type
    });
  }, [
    listTicketMessagesReq,
    supportTicketId,
    pagination,
    currentRole?.type
  ]);

  // Fetch support ticket
  useEffect(() => {
    getSupportTicketReq({ supportTicketId });
  }, [ supportTicketId, getSupportTicketReq ]);

  // Fetch support ticket messages
  useEffect(() => {
    listTicketMessagesReq({
      supportTicketId,
      pagination,
      currentUserRole: currentRole?.type
    });
  }, [
    supportTicketId,
    listTicketMessagesReq,
    pagination,
    currentRole?.type
  ]);

  // When getSupportTicketRes changes
  useEffect(() => {
    // If an error occurs show a toast
    if (getSupportTicketRes.error) {
      toast.error(getSupportTicketRes.error.message);
    }

    // On success
    if (getSupportTicketRes.data) {
      setSupportTicket(getSupportTicketRes.data);
    }
  }, [ getSupportTicketRes ]);

  // When sanitisedListTicketMessagesRes changes
  useEffect(() => {
    // If an error occurs show a toast
    if (sanitisedListTicketMessagesRes.error) {
      toast.error(sanitisedListTicketMessagesRes.error.message);
    }

    // On success
    if (sanitisedListTicketMessagesRes.data) {
      setTicketMessagesCount(sanitisedListTicketMessagesRes.data.count);

      setTicketMessages(existingTicketMessages => {
        return _.uniqBy(_.concat(sanitisedListTicketMessagesRes.data?.result || [], existingTicketMessages), "id");
      });
    }
  }, [ sanitisedListTicketMessagesRes ]);

  // When updateSupportTicketRes changes
  useEffect(() => {
    if (updateSupportTicketRes.error) {
      toast.error(updateSupportTicketRes.error.message);
      setIsSubmittingMsg(false);
    }

    // fetch all ticket messages when a new one has succesfully been created
    if (updateSupportTicketRes.data) {
      // reset subscribed to default
      setUserSubscribed(true);
      setSupportTicket(updateSupportTicketRes.data);
      toast.success("Successfully updated ticket");

      if (CreditsContextRef.current) {
        CreditsContextRef.current.setSupportTicket(updateSupportTicketRes.data);
      }

      if (MessageRef.current) {
        MessageRef.current.clearState();
      }

      setIsSubmittingMsg(false);

      listTicketMessagesReq({
        supportTicketId,
        pagination,
        currentUserRole: currentRole?.type
      });
    }
  }, [
    listTicketMessagesReq,
    pagination,
    updateSupportTicketRes,
    supportTicketId,
    currentRole?.type
  ]);

  useEffect(() => {
    if (closeSupportTicketRes.data) {
      listTicketMessagesReq({
        supportTicketId,
        pagination,
        currentUserRole: currentRole?.type
      });
      setSupportTicket(closeSupportTicketRes.data);

      if (CreditsContextRef.current) {
        CreditsContextRef.current.setSupportTicket(closeSupportTicketRes.data);
      }

      if (MessageRef.current) {
        MessageRef.current.clearState();
      }
    } else if (closeSupportTicketRes.error) {
      toast.error(closeSupportTicketRes.error.message);
    }
  }, [
    closeSupportTicketRes.data,
    closeSupportTicketRes.error,
    currentRole?.type,
    listTicketMessagesReq,
    pagination,
    supportTicketId,
    updateSupportTicketRes.data
  ]);

  const handleSaveTicket = (extraData?: Partial<UpdateSupportTicketParams>): void => {
    // check if content in message exists.
    // respondText is set by wysiwyg. Length of 8 is equal to initial content provided by wysiwyg which returns <p></p>.
    if (!timeTaken && respondText.length === 8 && attachmentKeys.length === 0) {
      return;
    }

    // build update params
    const updateData: UpdateSupportTicketParams = {
      ...extraData,
      message: respondText.length > 8 ? respondText : undefined,
      resolved: false,
      supportTicketId,
      evidence: attachmentKeys || []
    };

    // validate time taken and add to updateData if exists
    if (timeTaken) {
      updateData.timeTaken = timeTaken;
    }

    if (!userSubscribed) {
      updateData.unsubscribeUserId = currentUser.id;
    }

    updateSupportTicketReq(updateData);
    setIsSubmittingMsg(true);
    cleanRespondContainer();
  };

  const clearAttachmentsInDropzone = React.useCallback(() => {
    if (dropzoneRef.current) {
      dropzoneRef.current.clearFilesData();
    }
  }, [ dropzoneRef ]);

  const cleanRespondContainer = React.useCallback(() => {
    setRespondText("");
    setAttachmentKeys([]);
    clearAttachmentsInDropzone();
    setTimeTaken(null);
  }, [ clearAttachmentsInDropzone ]);

  const canCloseTicket = React.useMemo(() => {
    if (!supportTicket) {
      return false;
    } else if (supportTicket?.status === SupportTicketStatus.AdminResolved || supportTicket?.status === SupportTicketStatus.CustomerResolved) {
      return false;
    }

    return true;
  }, [ supportTicket ]);

  // Handles updates to the support ticket that originate from the support ticket sidebar UI
  const handleInfoBarUpdate = React.useCallback((updateType: SupportTicketSidebarUpdate, updateValue: number | SupportTicketPriority | null) => {
    if (supportTicket) {
      updateSupportTicketReq({
        supportTicketId: supportTicket.id,
        resolved: (supportTicket?.status === SupportTicketStatus.AdminResolved || supportTicket?.status === SupportTicketStatus.CustomerResolved),
        [ updateType ]: updateValue
      });
    }
  }, [ supportTicket, updateSupportTicketReq ]);

  const handleCloseTicket = React.useCallback(({ maintainCredits }: { maintainCredits?: boolean } = {}) => {
    // build close params
    const closeTicketData: CloseSupportTicketParams = {
      supportTicketId,
      message: respondText,
      maintainCredits
    };

    // validate time taken and add to closeTicketData if exists
    if (timeTaken) {
      closeTicketData.timeTaken = timeTaken;
    }

    closeSupportTicketReq(closeTicketData);
    cleanRespondContainer();
  }, [
    cleanRespondContainer,
    closeSupportTicketReq,
    respondText,
    supportTicketId,
    timeTaken
  ]);

  const closeButton = React.useMemo(() => {
    if (!canCloseTicket) {
      return <></>;
    }

    if (supportTicketTypeAllowsCredits) {
      const items = [];

      if (currentRole?.type === "admin") {
        items.push({
          title: "Mark as resolved",
          action: () => {
            handleCloseTicket({ maintainCredits: true });
          }
        });
      }

      items.push({
        title: "Close ticket",
        action: () => {
          if (CreditsContextRef.current) {
            CreditsContextRef.current.setShowCloseTicketWithCreditsModal(true);
          }
        }
      });

      return (
        <ActionButton
          firstActionClickable
          title="actions"
          buttonType="secondary"
          loading={closeSupportTicketRes.pending}
          items={items}
        />
      );
    }

    return (
      <Button
        $buttonType="plain"
        onClick={() => handleCloseTicket()}
        loading={closeSupportTicketRes.pending}
      >
        Close ticket
      </Button>
    );
  }, [
    canCloseTicket,
    closeSupportTicketRes.pending,
    handleCloseTicket,
    supportTicketTypeAllowsCredits
  ]);

  // generates submit button text based on state of time and message fields
  const submitButtonText = React.useMemo(() => {
    // respond text length check at 8 for wysiwig
    // default with no input
    let buttonText = "Submit Message";

    // with timeTaken filled but no message
    if (timeTaken && respondText.length < 9) {
      buttonText = "Submit Time";
    }

    // with both time and message filled
    if (timeTaken && respondText.length > 8) {
      buttonText = "Submit Message & Time";
    }

    return buttonText;
  }, [ respondText.length, timeTaken ]);

  return (
    <Layout
      {...props}
      title={`Support Ticket ${formatSupportTicketNumber(supportTicketId)}`}
    >
      <MainContentWrapper>
        {getSupportTicketRes.pending ? <LoadingSpinner type="circular" /> : (
          supportTicket && (
            <StyleWrapper
              direction="vertical"
              width="100%"
            >
              <Grid.Bounds direction="horizontal">

                <Grid.Bounds
                  direction="vertical"
                  className="left-container"
                >
                  {supportTicket.order && (
                    <Grid.Bounds
                      className="back-to-order"
                      direction="horizontal"
                    >
                      <OrderText onClick={() => history.push(`/orders/${supportTicket.orderId}`)}>
                        <Icon
                          icon={Icons.CHEVRONLEFT}
                          size={20}
                          $fillColor="darkGrey"
                        />

                        <Text
                          className="order-text"
                          fontSize="h2"
                          weight="light"
                          style={{ marginBottom: 0 }}
                        >
                          Order
                        </Text>

                        <Text
                          className="order-text"
                          fontSize="h2"
                          weight="bold"
                          style={{ marginBottom: 0 }}
                        >
                          {`#${supportTicket.orderId}`}
                        </Text>
                      </OrderText>

                      <DeliveryDate>
                        <Text weight="bold">
                          {"Delivery "}
                        </Text>

                        <Text className="order-text">
                          <Datetime
                            ignoreLocal
                            date={supportTicket.order?.orderDeliveryDate || null}
                            template="long_date"
                          />
                        </Text>

                        <Text
                          className="order-text"
                          weight="bold"
                        >
                          {currentOrderStatus && supportTicket.orderId && (
                            <OrderStatus
                              tooltipPlace="left"
                              orderId={supportTicket.orderId}
                              status={currentOrderStatus}
                            />
                          )}
                        </Text>
                      </DeliveryDate>
                    </Grid.Bounds>
                  )}

                  <SupportTicketDetails
                    title={supportTicket.supportTicketType?.name || ""}
                    createdAt={supportTicket.createdAt}
                    message={supportTicket.body}
                  />

                  {!!ticketMessages?.length && (
                    <TicketMessages direction="vertical">
                      {_.orderBy(ticketMessages, "createdAt", "desc")?.map(ticketMessage => (
                        <SupportTicketMessage
                          key={ticketMessage.id}
                          message={ticketMessage.message}
                          from={`${formatUserName(ticketMessage.user as IUser)}`}
                          createdAt={ticketMessage.createdAt}
                          documents={ticketMessage.documents || []}
                          internal={ticketMessage.internal}
                        />
                      ))}
                    </TicketMessages>
                  )}

                  {sanitisedListTicketMessagesRes.pending && (
                    <LoadingSpinner
                      type="flat"
                      position="inline"
                    />
                  )}

                  {ticketMessagesCount > ticketMessages?.length ? (
                    <Grid.Bounds
                      direction="vertical"
                      align="center"
                    >
                      <Grid.Box
                        align="center"
                        className="more-button-wrapper"
                      >
                        <Button
                          icon={Icons.CHEVRONDOWN}
                          iconPosition="right"
                          $buttonType="plain"
                          onClick={() => setPagination({
                            ...pagination,
                            offset: ticketMessages?.length
                          })}
                        >
                          Show more
                        </Button>
                      </Grid.Box>
                    </Grid.Bounds>
                  ) : null}

                  {currentRole?.type === "admin" && (
                    <Grid.Bounds
                      direction="vertical"
                      align="right"
                    >
                      <Grid.Box
                        align="right"
                        className={`add-private-note-wrapper ${ticketMessagesCount === 0 ? "private-note-spacing" : ""}`}
                      >
                        <Button
                          icon={Icons.NOTES}
                          iconPosition="right"
                          onClick={() => setShowNewInternalNoteModal(true)}
                        >
                          Add Private Note
                        </Button>
                      </Grid.Box>
                    </Grid.Bounds>
                  )}

                  <RespondTextAreaContainer
                    innerRef={MessageRef}
                    label="Respond to this ticket"
                    handleChange={(value => setRespondText(value))}
                    updateAttachments={setAttachmentKeys}
                    dropzoneRef={dropzoneRef}
                    subscribed={userSubscribed}
                    updateSubscribed={setUserSubscribed}
                    hideSubscribed={supportTicket.currentUserSubscribed}
                  >
                    <div className="respond-fields">
                      {currentRole?.type === "admin" && (
                        <>
                          <div className="time-taken-input">
                            <Text as="h4">
                              Time Taken:
                            </Text>

                            <Input
                              as="input"
                              inputType="number"
                              name="time-taken"
                              autoHideLabels
                              label="Time Taken"
                              placeholder=""
                              value={timeTaken || ""}
                              handleChange={e => {
                                setTimeTaken(e.target.value);
                              }}
                            />
                          </div>

                          <div className="time-taken-instruction">
                            <Text as="sub">
                              minutes
                            </Text>
                          </div>
                        </>
                      )}

                      <div className="actions">
                        {closeButton}

                        <Button
                          $buttonType="primary"
                          onClick={() => handleSaveTicket()}
                          loading={isSubmittingMsg}
                          disabled={respondText.length < 9 && !timeTaken} // empty respondText === 8 length set by wysiwyg
                        >
                          {submitButtonText}
                        </Button>
                      </div>
                    </div>

                  </RespondTextAreaContainer>

                </Grid.Bounds>

              </Grid.Bounds>
            </StyleWrapper>
          )
        )}

      </MainContentWrapper>

      <InfoBarWrapper>
        <SupportTicketSidebar
          data={supportTicket || null}
          isLoading={getSupportTicketRes.pending}
          terminology={props.terminology}
          handleUpdate={handleInfoBarUpdate}
          displayAdminContent={currentRole?.type === "admin"}
        />
      </InfoBarWrapper>

      {supportTicket && supportTicketTypeAllowsCredits && !getSupportTicketRes.pending && (
        <CreditRequestContextProvider
          ref={CreditsContextRef}
          supportTicket={supportTicket}
          saveSupportTicket={creditsData => handleSaveTicket(creditsData)}
          onCancelCreditRequest={() => handleCloseTicket()}
        />
      )}

      <SupportTicketInternalNoteModal
        open={showNewInternalNoteModal}
        supportTicketId={supportTicketId}
        resolved={(supportTicket?.status === SupportTicketStatus.AdminResolved || supportTicket?.status === SupportTicketStatus.CustomerResolved)}
        onDismiss={() => {
          setShowNewInternalNoteModal(false);
        }}
        onSuccess={handleInternalNoteSuccess}
      />
    </Layout>
  );
};

const OrderText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`;

const TicketMessages = styled(Grid.Bounds)`
  height: auto;
  margin-bottom: ${({ theme }) => theme.spacing.margins.lg};
`;

const DeliveryDate = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;

const StyleWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`

    .back-to-order {
      background: ${theme.colors.white};
      padding: ${theme.spacing.padding.sm};
      margin: ${theme.spacing.margins.sm} 0;
      border-radius: ${theme.borderRadius};
      align-items: center;
      justify-content: space-between;

      .order-text {
        margin-bottom: 0;
        margin-left: ${theme.spacing.margins.sm};
      }
    }

    .left-container {
      width: 100%;
    }

    .more-button-wrapper {
      display: flex;
      justify-content: center;
    }

    .add-private-note-wrapper {
      display: flex;
      justify-content: flex-end;

      .button-wrapper button {
        background-color: ${rgba(theme.colors.warn, 0.5)};
        border: 1px solid ${theme.colors.lightGrey};
        color: ${theme.colors.darkGrey};

        .icon {
          fill: ${theme.colors.darkGrey}!important;
        }
      }
    }

    .private-note-spacing {
      margin-top: ${theme.spacing.margins.lg};
    }

    .respond-fields {
      .actions {
        display: flex;
        justify-content: end;

        button {
          margin-left: ${theme.spacing.margins.sm};
        }
      }

      .time-taken-instruction {
        display: flex;
        justify-content: flex-end;
        margin-bottom: ${theme.spacing.margins.md};
      }
    }

    .time-taken-input {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-top: ${theme.spacing.margins.md};

      h4 {
        margin-bottom: 0;
        margin-right: ${theme.spacing.margins.sm};
      }
    }

  `};
`;

export default ViewSupportTicketPage;
